import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner, Modal } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getCustomer, updateCustomer, fetchEntityList } from '../services/api';
import { getCurrentUser } from '../services/authService'; // <-- Enabled import
import {
    sanitizeLocationFieldInput,
    validateLocationField,
} from '../lib/utils/validation';

// --- Helper: Debounce Hook ---
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => { setDebouncedValue(value); }, delay);
        return () => { clearTimeout(handler); };
    }, [value, delay]);
    return debouncedValue;
}

function EditCustomer() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validated, setValidated] = useState(false);
    // --- Modal State ---
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    // --- Form State (Only Personal/Profile Data) ---
    const [formData, setFormData] = useState({
        customerId: '',
        firstName: '',
        lastName: '',
        dob: '',
        gender: '',
        email: '', // Read-only usually
        recordId: '', // Device User ID
        zipcode: '',
        city: '',
        state: '',
        country: '',
        enrollmentDate: '' // Read-only
    });
    const [showRecordId, setShowRecordId] = useState(false);
    const [isFetchingZip, setIsFetchingZip] = useState(false);
    const debouncedZipcode = useDebounce(formData.zipcode, 500);
    const [entitiesList, setEntitiesList] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});

    // --- 1. Load Entities List (Needed for config check) ---
    useEffect(() => {
        const loadEntities = async () => {
            try {
                const response = await fetchEntityList();
                if (response.data && response.data.success && response.data.data?.items) {
                    setEntitiesList(response.data.data.items);
                }
            } catch (error) {
                console.error("Error loading entities:", error);
            }
        };
        loadEntities();
    }, []);

    // --- 1. Load Customer Data ---
    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);
                const response = await getCustomer(id);

                if (response.data && response.data.success) {
                    const c = response.data.data;

                    setFormData({
                        customerId: c.recordId,
                        firstName: c.firstName || '',
                        lastName: c.lastName || '',
                        dob: c.dob || '',
                        gender: c.gender || '',
                        email: c.email || '',
                        recordId: c.recordId || '',
                        zipcode: c.zipCode || '', // Note: Check API casing (zipCode vs zipcode)
                        city: c.city || '',
                        state: c.state || '',
                        country: c.country || '',
                        enrollmentDate: c.enrollmentDate ? c.enrollmentDate.split('T')[0] : '',
                    });

                    // --- VISIBILITY LOGIC ---
                    let isRecordIdVisible = false;

                    // Check Entity Config for Record ID Visibility
                    // Case 1: Entity object is nested in response
                    let entity = c.entityMaster || c.entity_master;

                    // Case 2: Only ID is provided, look it up in loaded list
                    if (!entity && c.entityMasterId && entitiesList.length > 0) {
                        // Try finding as Entity
                        entity = entitiesList.find(e => e.id === c.entityMasterId);

                        // If not found, try finding as Client
                        if (!entity) {
                            for (const ent of entitiesList) {
                                const client = ent.clients?.find(cl => cl.id === c.entityMasterId);
                                if (client) {
                                    entity = client;
                                    break;
                                }
                            }
                        }
                    }

                    if (entity) {
                        isRecordIdVisible = entity.enable_entity_assigned_record_id === true || entity.enableEntityAssignedRecordId === true;
                    }

                    // B. Client Admin Override (Use Local Storage)
                    const currentUser = getCurrentUser();
                    if (currentUser) {
                        const roleCode = currentUser.role?.code;
                        const accountType = currentUser.entityMaster?.account_type;

                        // If Client Admin, trust their local config
                        if (roleCode === 'CLIENT_ADMIN' || (roleCode === 'ADMIN' && accountType === 'CLIENT')) {
                            if (currentUser.entityMaster) {
                                isRecordIdVisible = currentUser.entityMaster.enable_entity_assigned_record_id === true || currentUser.entityMaster.enableEntityAssignedRecordId === true;
                            }
                        }
                    }

                    setShowRecordId(isRecordIdVisible);
                }
            } catch (error) {
                console.error("Error loading customer:", error);
                // alert("Failed to load customer details.");
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, [id, entitiesList]);

    // --- 2. Zipcode Auto-fill ---
    const fetchZipcodeData = useCallback(async (zip) => {
        if (!zip || zip.length < 5) return;
        setIsFetchingZip(true);
        try {
            const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
            if (!response.ok) throw new Error('Zipcode not found');
            const data = await response.json();
            const place = data.places[0];
            setFormData(prev => ({
                ...prev,
                city: place['place name'],
                state: place['state abbreviation'],
                country: data['country abbreviation']
            }));
        } catch (error) {
            console.error(error);
        } finally {
            setIsFetchingZip(false);
        }
    }, []);

    useEffect(() => { if (debouncedZipcode) fetchZipcodeData(debouncedZipcode); }, [debouncedZipcode, fetchZipcodeData]);

    // --- Handlers ---
    const validateLocationErrors = (data) => {
        const nextErrors = {};
        const zipErr = String(data.zipcode || '').trim()
            ? validateLocationField('zipCode', data.zipcode)
            : '';
        if (zipErr) nextErrors.zipcode = zipErr;
        const cityErr = String(data.city || '').trim()
            ? validateLocationField('city', data.city)
            : '';
        if (cityErr) nextErrors.city = cityErr;
        const stateErr = String(data.state || '').trim()
            ? validateLocationField('state', data.state)
            : '';
        if (stateErr) nextErrors.state = stateErr;
        const countryErr = String(data.country || '').trim()
            ? validateLocationField('country', data.country)
            : '';
        if (countryErr) nextErrors.country = countryErr;
        return nextErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const nextValue = ['zipcode', 'city', 'state', 'country'].includes(name)
            ? sanitizeLocationFieldInput(name === 'zipcode' ? 'zipCode' : name, value)
            : value;
        setFormData(prev => ({ ...prev, [name]: nextValue }));
        if (['zipcode', 'city', 'state', 'country'].includes(name)) {
            const locationFieldName = name === 'zipcode' ? 'zipCode' : name;
            const error = String(nextValue || '').trim()
                ? (validateLocationField(locationFieldName, nextValue) || '')
                : '';
            setFieldErrors(prev => ({ ...prev, [name]: error }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        const locationErrors = validateLocationErrors(formData);
        if (Object.keys(locationErrors).length > 0) {
            setFieldErrors(locationErrors);
            setValidated(true);
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');

        // Helper to convert empty strings to null
        const toNull = (value) => (value === '' || value === undefined ? null : value);

        // Construct payload with only the fields we want to update
        const payload = {
            firstName: toNull(formData.firstName),
            lastName: toNull(formData.lastName),
            dob: toNull(formData.dob),
            gender: toNull(formData.gender),
            zipCode: toNull(formData.zipcode),
            city: toNull(formData.city),
            state: toNull(formData.state),
            country: toNull(formData.country),
            // email: formData.email, // Include if API allows email updates
            // Only include recordId if visible
            recordId: toNull(formData.recordId)
        };

        try {
            await updateCustomer(id, payload);
            setShowSuccessModal(true);
        } catch (err) {
            console.error("Failed to update customer:", err);
            setErrorMessage(err.response?.data?.message || "Failed to update device user. Please try again.");
            setShowErrorModal(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSuccessClose = () => {
        setShowSuccessModal(false);
        navigate('/customers');
    };

    if (isLoading) {
        return <Container className="text-center py-5"><Spinner animation="border" variant="primary" /></Container>;
    }

    return (
        <Container fluid className="px-0">
            <Row>
                <Col>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white p-3">
                            <Row className="justify-content-between align-items-center">
                                <Col><h1 className="h3 mb-0 text-primary">Edit Device User</h1></Col>
                                <Col className="text-end"><span className="text-muted">Customer ID: <span style={{ fontWeight: "bold" }}>{formData.customerId}</span></span></Col>
                            </Row>
                        </Card.Header>
                        <Card.Body className="p-4 p-md-5">
                            <Form noValidate validated={validated} onSubmit={handleSubmit}>

                                {/* <h5 className="mb-4 text-secondary">Profile Information</h5> */}
                                {/* Device User ID (Record ID) - Conditional */}
                                {showRecordId && (
                                    <Row className="mb-3">
                                        <Form.Group as={Col} md={6} controlId="recordId">
                                            <Form.Label>Device User ID*</Form.Label>
                                            <Form.Control
                                                required
                                                name="recordId"
                                                value={formData.recordId}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Row>
                                )}

                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6} controlId="firstName">
                                        <Form.Label>First Name*</Form.Label>
                                        <Form.Control required name="firstName" value={formData.firstName} onChange={handleChange} />
                                    </Form.Group>
                                    <Form.Group as={Col} md={6} controlId="lastName">
                                        <Form.Label>Last Name*</Form.Label>
                                        <Form.Control required name="lastName" value={formData.lastName} onChange={handleChange} />
                                    </Form.Group>
                                </Row>

                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6} controlId="email">
                                        <Form.Label>Email Address</Form.Label>
                                        <Form.Control readOnly disabled name="email" value={formData.email} className="bg-light" />
                                    </Form.Group>
                                    <Form.Group as={Col} md={3} controlId="dob">
                                        <Form.Label>Date of Birth*</Form.Label>
                                        <Form.Control required type="date" name="dob" value={formData.dob} onChange={handleChange} />
                                    </Form.Group>
                                    <Form.Group as={Col} md={3} controlId="gender">
                                        <Form.Label>Gender</Form.Label>
                                        <Form.Select name="gender" value={formData.gender} onChange={handleChange}>
                                            <option value="">...</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Row>

                                <Row className="mb-4">
                                    <Form.Group as={Col} md={3} controlId="zipcode">
                                        <Form.Label>Zipcode</Form.Label>
                                        <div className="d-flex align-items-center">
                                            <Form.Control
                                                name="zipcode"
                                                value={formData.zipcode}
                                                onChange={handleChange}
                                                isInvalid={!!fieldErrors.zipcode}
                                                maxLength={20}
                                            />
                                            {isFetchingZip && <Spinner animation="border" size="sm" className="ms-2" />}
                                        </div>
                                        <Form.Control.Feedback type="invalid" style={{ display: fieldErrors.zipcode ? 'block' : 'none' }}>
                                            {fieldErrors.zipcode}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group as={Col} md={3} controlId="city">
                                        <Form.Label>City</Form.Label>
                                        <Form.Control
                                            readOnly
                                            name="city"
                                            value={formData.city}
                                            className="bg-light"
                                            isInvalid={!!fieldErrors.city}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {fieldErrors.city}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group as={Col} md={3} controlId="state">
                                        <Form.Label>State</Form.Label>
                                        <Form.Control
                                            readOnly
                                            name="state"
                                            value={formData.state}
                                            className="bg-light"
                                            isInvalid={!!fieldErrors.state}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {fieldErrors.state}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group as={Col} md={3} controlId="country">
                                        <Form.Label>Country</Form.Label>
                                        <Form.Control
                                            readOnly
                                            name="country"
                                            value={formData.country}
                                            className="bg-light"
                                            isInvalid={!!fieldErrors.country}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {fieldErrors.country}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Row>

                                <hr className="my-4" />
                                <div className="d-flex justify-content-end gap-2">
                                    <Button as={Link} to="/customers" variant="light" className="border">
                                        Cancel
                                    </Button>
                                    <Button variant="success" type="submit" disabled={isSubmitting} className="text-white">
                                        {isSubmitting ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving...</> : 'Save Changes'}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* --- Modals --- */}
            <Modal show={showSuccessModal} onHide={handleSuccessClose} centered backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title className="text-success">Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>Device User profile updated successfully!</Modal.Body>
                <Modal.Footer>
                    <Button variant="success" onClick={handleSuccessClose}>OK</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-danger">Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>{errorMessage || 'Something went wrong.'}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowErrorModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default EditCustomer;